import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { X, ThumbsUp, ThumbsDown, Eye, Calendar } from 'lucide-react';

const HelpArticleModal = ({ article, isOpen, onClose, onVote }) => {
  const [hasVoted, setHasVoted] = useState(false);
  const [voteType, setVoteType] = useState(null);

  if (!isOpen || !article) return null;

  const handleVote = (helpful) => {
    if (hasVoted) return;
    
    setHasVoted(true);
    setVoteType(helpful);
    onVote(article.id, helpful);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <CardHeader className="bg-teal-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Badge className="bg-white bg-opacity-20 text-white">
                  {article.category}
                </Badge>
                <div className="flex items-center text-sm text-teal-100">
                  <Eye className="w-4 h-4 mr-1" />
                  {article.views} views
                </div>
              </div>
              <CardTitle className="text-xl">{article.title}</CardTitle>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {/* Article Content */}
          <div className="prose max-w-none mb-8">
            <div className="text-gray-800 leading-relaxed whitespace-pre-line">
              {article.content}
            </div>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Related topics:</h4>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-teal-600 border-teal-300">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Article Stats and Voting */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <Eye className="w-4 h-4 mr-1" />
                  {article.views} views
                </div>
                <div className="flex items-center">
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  {article.helpful_votes} found this helpful
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 mr-2">Was this helpful?</span>
                <Button
                  size="sm"
                  variant={voteType === true ? "default" : "outline"}
                  onClick={() => handleVote(true)}
                  disabled={hasVoted}
                  className={voteType === true ? "bg-green-500 hover:bg-green-600 text-white" : "hover:bg-green-50 hover:border-green-300"}
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Yes
                </Button>
                <Button
                  size="sm"
                  variant={voteType === false ? "default" : "outline"}
                  onClick={() => handleVote(false)}
                  disabled={hasVoted}
                  className={voteType === false ? "bg-red-500 hover:bg-red-600 text-white" : "hover:bg-red-50 hover:border-red-300"}
                >
                  <ThumbsDown className="w-4 h-4 mr-1" />
                  No
                </Button>
              </div>
            </div>

            {hasVoted && (
              <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm text-teal-800">
                  {voteType ? "Thank you for your feedback!" : "We're sorry this wasn't helpful. Please contact our support team for more assistance."}
                </p>
              </div>
            )}
          </div>

          {/* Related Actions */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-3">Still need help?</h4>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="hover:bg-teal-50">
                Contact Support
              </Button>
              <Button size="sm" variant="outline" className="hover:bg-teal-50">
                Start Live Chat
              </Button>

            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpArticleModal;